import {
  Dispatch,
  useEffect,
  useState,
  useRef,
  useCallback,
  SetStateAction
} from "react";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { getBraintreeCCClientToken } from "@tvg/api/paws/braintreeCC";
import { useIsomorphicLayoutEffect } from "@tvg/custom-hooks";
import { ErrorMessage } from "@tvg/wallet/src/types";
import { getAccountNumber } from "@urp/store-selectors";
import useIsBraintreeEnabled from "./useIsBraintreeEnabled";
import {
  BRAINTREE_ERROR_CODES,
  BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS,
  BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS
} from "../constants";
import { createBraintreeClient } from "../utils/braintreeClient";
import {
  getCachedClientToken,
  setCachedClientToken,
  invalidateCachedClientToken
} from "../utils/braintreeClientTokenCache";
import type {
  BraintreeClientInstance,
  BraintreeDataCollectorInstance,
  BraintreeHostedFieldsInstance,
  HostedFieldsState,
  HostedFieldEvent,
  HostedFieldsCreateConfig,
  HostedFieldsTokenizePayload
} from "../types/braintree";
import "../types/braintree";

const DEFAULT_FIELD_STATE = {
  container: null,
  isFocused: false,
  isEmpty: true,
  isPotentiallyValid: true,
  isValid: false
};

const DEFAULT_FIELDS_STATE: HostedFieldsState = {
  cardholderName: { ...DEFAULT_FIELD_STATE },
  number: { ...DEFAULT_FIELD_STATE },
  expirationDate: { ...DEFAULT_FIELD_STATE },
  cvv: { ...DEFAULT_FIELD_STATE }
};

interface UseBraintreeHostedFieldsProps {
  isModalOpen: boolean;
  styles: Record<string, Record<string, string>>;
  setErrorMessage: Dispatch<SetStateAction<ErrorMessage>>;
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
}

export const fetchAndValidateClientToken = async (
  accountId: string,
  setErrorMessage: Dispatch<SetStateAction<ErrorMessage>>,
  signal?: AbortSignal,
  forceRefresh: boolean = false
): Promise<{ token: string } | null> => {
  if (!forceRefresh) {
    const cachedToken = getCachedClientToken(accountId);
    if (cachedToken) return { token: cachedToken };
  }

  try {
    const response = await getBraintreeCCClientToken(accountId);
    const token = get(response, "data.token");

    if (!token || typeof token !== "string") {
      throw new Error("Invalid client token format");
    }

    setCachedClientToken(accountId, token);
    return { token };
  } catch (err) {
    if (signal?.aborted) return null;

    const errorCode = String(
      get(err, "response.data.code") || BRAINTREE_ERROR_CODES.INIT_ERROR
    );

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false
    }));
  }

  return null;
};

export const useBraintreeHostedFields = ({
  isModalOpen,
  styles,
  setErrorMessage,
  onFieldFocus,
  onFieldBlur
}: UseBraintreeHostedFieldsProps) => {
  const accountId = useSelector(getAccountNumber);
  const { isEnabled: isBraintreeCCEnabled, isResolved: isBraintreeResolved } =
    useIsBraintreeEnabled();

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldStates, setFieldStates] =
    useState<HostedFieldsState>(DEFAULT_FIELDS_STATE);

  const [deviceData, setDeviceData] = useState("");
  const [isSdkAvailable, setIsSdkAvailable] = useState(
    typeof window !== "undefined" &&
      !!window.braintree?.hostedFields &&
      !!window.braintree?.dataCollector
  );

  const [isContainerReady, setIsContainerReady] = useState(false);
  const containerRef = useCallback((node: HTMLElement | null) => {
    setIsContainerReady(!!node);
  }, []);

  useEffect(() => {
    if (
      isSdkAvailable ||
      !isBraintreeResolved ||
      !isBraintreeCCEnabled ||
      !isModalOpen ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (window.braintree?.hostedFields && window.braintree?.dataCollector) {
        clearTimeout(timeoutId);
        setIsSdkAvailable(true);
        clearInterval(intervalId);
      }
    }, BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setErrorMessage((prevState) => ({
        ...prevState,
        isOpen: true,
        errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
        isRetry: false
      }));
    }, BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [
    isSdkAvailable,
    isBraintreeResolved,
    isBraintreeCCEnabled,
    isModalOpen,
    setErrorMessage
  ]);

  const hostedFieldsInstanceRef = useRef<BraintreeHostedFieldsInstance | null>(
    null
  );
  const clientInstanceRef = useRef<BraintreeClientInstance | null>(null);
  const dataCollectorInstanceRef =
    useRef<BraintreeDataCollectorInstance | null>(null);
  const isInitializingRef = useRef(false);
  const teardownPromiseRef = useRef<Promise<void> | null>(null);
  const onFieldFocusRef = useRef(onFieldFocus);
  onFieldFocusRef.current = onFieldFocus;
  const onFieldBlurRef = useRef(onFieldBlur);
  onFieldBlurRef.current = onFieldBlur;

  const teardown = useCallback(() => {
    const promises: Promise<void>[] = [];

    if (hostedFieldsInstanceRef.current) {
      promises.push(hostedFieldsInstanceRef.current.teardown().catch(() => {}));
      hostedFieldsInstanceRef.current = null;
    }
    if (dataCollectorInstanceRef.current) {
      promises.push(
        dataCollectorInstanceRef.current.teardown().catch(() => {})
      );
      dataCollectorInstanceRef.current = null;
    }
    if (clientInstanceRef.current) {
      promises.push(clientInstanceRef.current.teardown().catch(() => {}));
      clientInstanceRef.current = null;
    }

    teardownPromiseRef.current =
      promises.length > 0 ? Promise.all(promises).then(() => {}) : null;

    setIsReady(false);
    setIsLoading(false);
    setDeviceData("");
    setFieldStates(DEFAULT_FIELDS_STATE);
    isInitializingRef.current = false;
  }, []);

  const tokenize =
    useCallback(async (): Promise<HostedFieldsTokenizePayload> => {
      if (!hostedFieldsInstanceRef.current) {
        throw new Error("Hosted Fields not initialized");
      }

      const payload = await hostedFieldsInstanceRef.current.tokenize();
      return payload;
    }, []);

  useEffect(() => {
    if (!isModalOpen || !isBraintreeCCEnabled) {
      teardown();
      isInitializingRef.current = false;
    }
  }, [isModalOpen, isBraintreeCCEnabled, teardown]);

  useEffect(() => () => teardown(), [teardown]);

  useIsomorphicLayoutEffect(() => {
    if (
      !isBraintreeCCEnabled ||
      !isModalOpen ||
      !accountId ||
      isReady ||
      isInitializingRef.current ||
      !isSdkAvailable ||
      !isContainerReady
    ) {
      return undefined;
    }

    const abortController = new AbortController();

    const tryInit = async (forceRefresh: boolean): Promise<void> => {
      const tokenData = await fetchAndValidateClientToken(
        accountId,
        setErrorMessage,
        abortController.signal,
        forceRefresh
      );
      if (!tokenData || abortController.signal.aborted) {
        return;
      }

      const clientInstance = await createBraintreeClient(tokenData.token);
      if (abortController.signal.aborted) {
        clientInstance.teardown().catch(() => {});
        return;
      }
      clientInstanceRef.current = clientInstance;

      const hostedFields = window.braintree!.hostedFields!;

      const fieldsConfig: HostedFieldsCreateConfig = {
        client: clientInstance,
        styles,
        fields: {
          cardholderName: {
            container: "#braintree-cardholder-name",
            placeholder: "Name as it appears on card"
          },
          number: {
            container: "#braintree-card-number",
            placeholder: "0000 0000 0000 0000"
          },
          expirationDate: {
            container: "#braintree-expiration-date",
            placeholder: "MM/YY"
          },
          cvv: {
            container: "#braintree-cvv",
            placeholder: "3 digits"
          }
        }
      };

      const hostedFieldsInstance = await hostedFields.create(fieldsConfig);
      if (abortController.signal.aborted) {
        hostedFieldsInstance.teardown().catch(() => {});
        clientInstance.teardown().catch(() => {});
        clientInstanceRef.current = null;
        return;
      }
      hostedFieldsInstanceRef.current = hostedFieldsInstance;

      hostedFieldsInstance.on("validityChange", (event: HostedFieldEvent) => {
        setFieldStates((prev) => ({ ...prev, ...event.fields }));
      });

      hostedFieldsInstance.on("empty", (event: HostedFieldEvent) => {
        setFieldStates((prev) => ({ ...prev, ...event.fields }));
      });

      hostedFieldsInstance.on("notEmpty", (event: HostedFieldEvent) => {
        setFieldStates((prev) => ({ ...prev, ...event.fields }));
      });

      hostedFieldsInstance.on("focus", (event: HostedFieldEvent) => {
        setFieldStates((prev) => ({ ...prev, ...event.fields }));
        onFieldFocusRef.current?.(event.emittedBy);
      });

      hostedFieldsInstance.on("blur", (event: HostedFieldEvent) => {
        setFieldStates((prev) => ({ ...prev, ...event.fields }));
        onFieldBlurRef.current?.(event.emittedBy);
      });

      if (!window.braintree?.dataCollector) {
        throw new Error("Braintree Data Collector SDK not loaded");
      }

      const dataCollectorInstance = await window.braintree.dataCollector.create(
        {
          client: clientInstance
        }
      );
      if (abortController.signal.aborted) {
        dataCollectorInstance.teardown().catch(() => {});
        return;
      }
      dataCollectorInstanceRef.current = dataCollectorInstance;
      setDeviceData(dataCollectorInstance.deviceData);

      setErrorMessage((prevState) => ({
        ...prevState,
        isOpen: false,
        errorCode: ""
      }));
      setIsLoading(false);
      setIsReady(true);
    };

    const initializeHostedFields = async () => {
      isInitializingRef.current = true;
      setIsLoading(true);

      try {
        if (teardownPromiseRef.current) {
          await teardownPromiseRef.current;
          teardownPromiseRef.current = null;
        }
        if (abortController.signal.aborted) return;

        const usedCachedToken = !!getCachedClientToken(accountId);

        try {
          await tryInit(false);
        } catch (err) {
          if (abortController.signal.aborted || !usedCachedToken) throw err;
          // Cached token likely stale (Braintree SDK rejected it). Drop the
          // partial init, invalidate the cache, and retry once with a fresh
          // token. If that fails too, fall through to the outer catch.
          invalidateCachedClientToken();
          teardown();
          if (teardownPromiseRef.current) {
            await teardownPromiseRef.current;
            teardownPromiseRef.current = null;
          }
          if (abortController.signal.aborted) return;
          isInitializingRef.current = true;
          setIsLoading(true);
          await tryInit(true);
        }
      } catch (err: unknown) {
        if (abortController.signal.aborted) return;
        teardown();
        setErrorMessage((prevState) => ({
          ...prevState,
          isOpen: true,
          errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
          isRetry: false
        }));
      } finally {
        isInitializingRef.current = false;
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    initializeHostedFields();

    return () => {
      abortController.abort();
    };
  }, [
    isBraintreeCCEnabled,
    isModalOpen,
    isReady,
    isSdkAvailable,
    isContainerReady,
    accountId,
    styles,
    setErrorMessage
  ]);

  return {
    isReady,
    isLoading,
    fieldStates,
    deviceData,
    tokenize,
    teardown,
    containerRef
  };
};
